/**
 * VehicleWindowProjectionView — three-panel vehicle investigation view.
 *
 * Renders the VehicleWindowProjection response:
 *   1. Opening State (collapsed card)
 *   2. In-Period Changes (timeline + orders tabs)
 *   3. Closing State (collapsed card)
 *   + Data Quality sidebar
 *
 * Drop-in replacement for VehicleInvestigationTimeline that uses the
 * new window-based projection endpoint.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Search, RefreshCw, Package, Fuel, User,
  ChevronDown, ChevronRight, MapPin, Clock,
  AlertTriangle, CheckCircle2, Circle, XCircle
} from 'lucide-react';
import type {
  VehicleWindowProjection,
  OrderLifecycle,
  TimelineEvent,
  GpsEvidence,
  DataQualityReport,
  OpeningState,
  ClosingState,
} from '../../types/vehicleInvestigation';
import { fetchVehicleWindowProjection, fetchVehicleWindowProjectionByDate } from '../../services/vehicleInvestigationAdapter';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

interface VehicleWindowProjectionViewProps {
  vehicleId: string;
  /** ISO from/to for arbitrary window, or date (YYYY-MM-DD) for daily */
  from?: string;
  to?: string;
  date?: string;
  displayName?: string;
}

// ── GPS Evidence Badge ───────────────────────────────────────────────
function EvidenceBadge({ evidence }: { evidence: GpsEvidence }) {
  const config = {
    gps_confirmed:   { label: 'GPS Confirmed',   cls: 'bg-success/20 text-success border-success/30' },
    graph_confirmed: { label: 'Graph Confirmed', cls: 'bg-ring/20 text-ring border-ring/30' },
    weak:            { label: 'Weak',            cls: 'bg-warning/20 text-warning border-warning/30' },
    missing:         { label: 'Missing',         cls: 'bg-muted-foreground/20 text-muted-foreground border-muted-foreground/30' },
    pending:         { label: 'Pending',         cls: 'bg-ring/20 text-ring border-ring/30' },
    not_applicable:  { label: 'N/A',             cls: 'bg-muted-foreground/20 text-muted-foreground border-muted-foreground/30' },
  }[evidence.status];
  return (
    <span className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium border ${config.cls}`}>
      {config.label}
    </span>
  );
}

// ── Exit State Badge ─────────────────────────────────────────────────
function ExitStateBadge({ state }: { state: string }) {
  const config: Record<string, { label: string; cls: string }> = {
    delivered:              { label: 'Delivered',         cls: 'text-success' },
    delivered_after_window: { label: 'Delivered (after)', cls: 'text-ring' },
    on_vehicle:             { label: 'On Vehicle',        cls: 'text-warning' },
    cancelled:              { label: 'Cancelled',         cls: 'text-destructive' },
  };
  const c = config[state] || { label: state, cls: 'text-muted-foreground' };
  return <span className={`font-medium ${c.cls}`}>{c.label}</span>;
}

// ── Confidence Score ─────────────────────────────────────────────────
function ConfidenceBar({ score }: { score: number }) {
  const color = score >= 70 ? 'bg-success' : score >= 40 ? 'bg-warning' : 'bg-destructive';
  return (
    <div className="flex items-center gap-2">
      <div className="w-16 h-1.5 bg-secondary rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full`} style={{ width: `${score}%` }} />
      </div>
      <span className="text-xs text-muted-foreground">{score}%</span>
    </div>
  );
}

// ── Timeline Category Icon ───────────────────────────────────────────
function CategoryIcon({ category }: { category: string }) {
  const cls = 'w-3.5 h-3.5';
  // Timeline categories are categorical (not status-meaningful), so they read
  // from the shared Chart palette — each keeps its legacy hue family at the
  // matching tab10 slot.
  switch (category) {
    case 'order': return <Package className={`${cls} text-chart-2`} />; // orange
    case 'fuel': return <Fuel className={`${cls} text-chart-9`} />; // yellow
    case 'driver': return <User className={`${cls} text-chart-1`} />; // blue
    case 'location': return <MapPin className={`${cls} text-chart-3`} />; // green
    default: return <Circle className={`${cls} text-muted-foreground`} />;
  }
}

// ── Format time helper ───────────────────────────────────────────────
function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

function formatDateTime(iso: string): string {
  try {
    const d = new Date(iso);
    return `${d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
  } catch {
    return iso;
  }
}

// ═════════════════════════════════════════════════════════════════════
// State Snapshot Card (Opening / Closing)
// ═════════════════════════════════════════════════════════════════════

function StateSnapshotCard({
  title,
  state,
  extra,
}: {
  title: string;
  state: OpeningState | ClosingState;
  extra?: React.ReactNode;
}) {
  const [expanded, setExpanded] = useState(false);
  const Icon = expanded ? ChevronDown : ChevronRight;

  return (
    <div className="bg-card/50 rounded-lg border border-border/50">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => setExpanded(!expanded)}
        className="w-full h-auto justify-between p-3 text-left rounded-lg hover:bg-secondary/20"
      >
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium text-white">{title}</span>
          <span className="text-xs text-muted-foreground">{formatDateTime(state.asOf)}</span>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {state.activeDriver && (
            <span className="flex items-center gap-1">
              <User className="w-3 h-3" />
              {state.activeDriver.name || state.activeDriver.entityId.substring(0, 8)}
            </span>
          )}
          <span className="flex items-center gap-1">
            <Package className="w-3 h-3" />
            {state.onboardOrders.length} orders
          </span>
          {state.location && (
            <span className="flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {state.location.confidence}
            </span>
          )}
        </div>
      </Button>

      {expanded && (
        <div className="px-3 pb-3 border-t border-border/30 space-y-3">
          {/* Location */}
          {state.location && (
            <div className="text-xs text-muted-foreground mt-2">
              <span className="text-muted-foreground">Location: </span>
              {state.location.lat.toFixed(4)}, {state.location.lon.toFixed(4)}
              <span className="ml-1 text-muted-foreground">({state.location.source}, {state.location.confidence})</span>
            </div>
          )}

          {/* Onboard orders */}
          {state.onboardOrders.length > 0 && (
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Onboard Orders</p>
              <div className="space-y-1">
                {state.onboardOrders.map(o => (
                  <div key={o.orderId} className="flex items-center justify-between text-xs text-foreground">
                    <span className="font-mono">{o.reference || o.orderId.substring(0, 12)}</span>
                    <span className="text-muted-foreground capitalize">{o.status} ({o.source})</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Fuel */}
          {state.estimatedFuel && (
            <div className="text-xs text-muted-foreground">
              <span className="text-muted-foreground">Estimated Fuel: </span>
              {state.estimatedFuel.litres.toFixed(1)}L ({state.estimatedFuel.confidence})
            </div>
          )}

          {extra}
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// Timeline Panel
// ═════════════════════════════════════════════════════════════════════

function TimelinePanel({ events }: { events: TimelineEvent[] }) {
  if (events.length === 0) {
    return <p className="text-sm text-muted-foreground py-4 text-center">No events during this window.</p>;
  }

  return (
    <div className="space-y-1 max-h-96 overflow-y-auto">
      {events.map((evt, i) => (
        <div key={i} className="flex items-start gap-2 py-1.5 px-2 hover:bg-secondary/20 rounded text-xs">
          <span className="text-muted-foreground font-mono w-12 shrink-0">{formatTime(evt.timestamp)}</span>
          <CategoryIcon category={evt.category} />
          <span className="text-foreground flex-1">{evt.summary}</span>
          {/* `source` is a provenance tier (Constitution §13.2) — an inference is
              not a confirmation, so it wears the violet provenance token. */}
          <span className={`text-xs capitalize ${
            evt.source === 'observed' ? 'text-provenance-observed' :
            evt.source === 'declared' ? 'text-provenance-declared' : 'text-provenance-inferred'
          }`}>{evt.source}</span>
        </div>
      ))}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// Order Lifecycles Panel
// ═════════════════════════════════════════════════════════════════════

function OrderLifecyclesPanel({ lifecycles }: { lifecycles: OrderLifecycle[] }) {
  if (lifecycles.length === 0) {
    return <p className="text-sm text-muted-foreground py-4 text-center">No orders linked during this window.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs text-left">
        <thead>
          <tr className="text-muted-foreground border-b border-border">
            <th className="py-2 pr-3 font-medium">Order</th>
            <th className="py-2 pr-3 font-medium">Entry</th>
            <th className="py-2 pr-3 font-medium">Pickup</th>
            <th className="py-2 pr-3 font-medium">Delivery</th>
            <th className="py-2 pr-3 font-medium">Exit</th>
            <th className="py-2 pr-3 font-medium">Confidence</th>
            <th className="py-2 font-medium">Window</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/50">
          {lifecycles.map(o => (
            <tr key={o.orderId} className="text-foreground hover:bg-secondary/20">
              <td className="py-2 pr-3 font-mono text-xs">
                {o.reference || (o.orderId.length > 12 ? `${o.orderId.substring(0, 12)}...` : o.orderId)}
              </td>
              <td className="py-2 pr-3">
                <span className={o.entryState === 'carried_over' ? 'text-ring' : 'text-success'}>
                  {o.entryState === 'carried_over' ? 'Carried Over' : 'Loaded'}
                </span>
              </td>
              <td className="py-2 pr-3">
                <div className="flex flex-col gap-0.5">
                  {o.actualLoadHubName ? (
                    <span className="text-ring text-xs">{o.actualLoadHubName}</span>
                  ) : o.pickupPostcode ? (
                    <span className="text-foreground text-xs">{o.pickupPostcode}</span>
                  ) : null}
                  <EvidenceBadge evidence={o.pickupEvidence} />
                </div>
              </td>
              <td className="py-2 pr-3">
                <div className="flex flex-col gap-0.5">
                  {o.deliveryPostcode && (
                    <span className="text-foreground text-xs">{o.deliveryPostcode}</span>
                  )}
                  <EvidenceBadge evidence={o.deliveryEvidence} />
                </div>
              </td>
              <td className="py-2 pr-3">
                <ExitStateBadge state={o.exitState} />
              </td>
              <td className="py-2 pr-3">
                <ConfidenceBar score={o.deliveryConfidenceScore} />
              </td>
              <td className="py-2">
                <span className="text-xs text-muted-foreground capitalize">
                  {o.windowRelationship.replace(/_/g, ' ')}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// Data Quality Panel
// ═════════════════════════════════════════════════════════════════════

function DataQualityPanel({ report }: { report: DataQualityReport }) {
  const color = report.completeness >= 80 ? 'bg-success' : report.completeness >= 50 ? 'bg-warning' : 'bg-destructive';

  return (
    <div className="space-y-3">
      {/* Completeness bar */}
      <div className="flex items-center gap-3">
        <span className="text-xs text-muted-foreground">Completeness</span>
        <div className="flex-1 h-2 bg-secondary rounded-full overflow-hidden">
          <div className={`h-full ${color} rounded-full`} style={{ width: `${report.completeness}%` }} />
        </div>
        <span className="text-sm text-white font-medium">{report.completeness}%</span>
      </div>

      {/* Issues */}
      {report.issues.length > 0 && (
        <div className="space-y-1">
          {report.issues.map((issue, i) => (
            <div key={i} className={`flex items-start gap-2 text-xs p-2 rounded ${
              issue.severity === 'error' ? 'bg-destructive/10 text-destructive' :
              issue.severity === 'warning' ? 'bg-warning/10 text-warning' :
              'bg-secondary/30 text-muted-foreground'
            }`}>
              {issue.severity === 'error' ? <XCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> :
               issue.severity === 'warning' ? <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> :
               <Circle className="w-3.5 h-3.5 shrink-0 mt-0.5" />}
              <span>{issue.message}</span>
            </div>
          ))}
        </div>
      )}

      {report.issues.length === 0 && (
        <div className="flex items-center gap-2 text-xs text-success">
          <CheckCircle2 className="w-3.5 h-3.5" />
          No quality issues detected
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// Main Component
// ═════════════════════════════════════════════════════════════════════

type ChangesTab = 'timeline' | 'orders' | 'quality';

export default function VehicleWindowProjectionView({
  vehicleId,
  from,
  to,
  date,
  displayName,
}: VehicleWindowProjectionViewProps) {
  const [data, setData] = useState<VehicleWindowProjection | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [changesTab, setChangesTab] = useState<ChangesTab>('orders');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let result: VehicleWindowProjection;
      if (from && to) {
        result = await fetchVehicleWindowProjection(vehicleId, from, to);
      } else if (date) {
        result = await fetchVehicleWindowProjectionByDate(vehicleId, date);
      } else {
        throw new Error('Either from/to or date must be provided');
      }
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load vehicle window projection');
    } finally {
      setLoading(false);
    }
  }, [vehicleId, from, to, date]);

  useEffect(() => {
    load();
  }, [load]);

  const deliveredCount = data?.changes.orderLifecycles.filter(
    o => o.exitState === 'delivered' || o.exitState === 'delivered_after_window',
  ).length ?? 0;

  return (
    <div className="bg-secondary/50 rounded-lg p-4 border border-border">
      {/* Section header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Search className="w-5 h-5 text-warning" />
          <h4 className="text-lg font-semibold text-white">Vehicle Investigation</h4>
          {displayName && <span className="text-sm text-muted-foreground ml-1">— {displayName}</span>}
          {data?.registration && (
            <span className="text-sm font-mono text-warning bg-warning/10 px-2 py-0.5 rounded">
              {data.registration}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {data?.provenance && (
            <span className="text-xs text-muted-foreground">
              {data.provenance.queryDurationMs}ms
            </span>
          )}
          <Button
            type="button"
            variant="ghost"
            size="iconSm"
            onClick={load}
            disabled={loading}
            className="rounded-lg"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 text-muted-foreground ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Window info */}
      {data && (
        <div className="flex items-center gap-2 mb-4 text-xs text-muted-foreground">
          <Clock className="w-3.5 h-3.5" />
          <span>{formatDateTime(data.investigationWindow.from)}</span>
          <span className="text-muted-foreground">→</span>
          <span>{formatDateTime(data.investigationWindow.to)}</span>
          <span className="mx-2 text-muted-foreground">|</span>
          <span className="text-warning">{data.changes.orderLifecycles.length} orders</span>
          <span className="text-success">{deliveredCount} delivered</span>
          <span className="text-foreground">{data.changes.timeline.length} events</span>
        </div>
      )}

      {/* Loading state */}
      {loading && !data && (
        <div className="space-y-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="flex items-start gap-3 animate-pulse">
              <div className="w-6 h-6 rounded-full bg-accent/50 shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-accent/50 rounded w-2/3" />
                <div className="h-3 bg-accent/30 rounded w-full" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Error state */}
      {error && !data && (
        <div className="bg-destructive/10 border-destructive/20 rounded-lg p-4 text-center">
          <p className="text-destructive text-sm mb-2">{error}</p>
          <Button type="button" variant="link" size="sm" onClick={load} className="text-destructive hover:text-destructive/80">
            Retry
          </Button>
        </div>
      )}

      {/* Main content */}
      {data && (
        <div className="space-y-4">
          {/* Panel 1: Opening State */}
          <StateSnapshotCard title="Opening State" state={data.openingState} />

          {/* Panel 2: In-Period Changes */}
          <div className="bg-card/30 rounded-lg border border-border/50">
            {/* Tab buttons */}
            <div className="flex gap-1 p-1 border-b border-border/50">
              {([
                { key: 'orders' as const, label: 'Orders', count: data.changes.orderLifecycles.length },
                { key: 'timeline' as const, label: 'Timeline', count: data.changes.timeline.length },
                { key: 'quality' as const, label: 'Data Quality', count: data.dataQuality.issues.length },
              ]).map(({ key, label, count }) => (
                <Button
                  key={key}
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setChangesTab(key)}
                  className={cn(
                    'rounded',
                    changesTab === key
                      ? 'bg-warning/30 text-warning'
                      : 'text-muted-foreground hover:text-foreground hover:bg-secondary',
                  )}
                >
                  {label}
                  {count > 0 && (
                    <Badge tone="warning">
                      {count}
                    </Badge>
                  )}
                </Button>
              ))}
            </div>

            {/* Tab content */}
            <div className="p-4">
              {changesTab === 'orders' && (
                <OrderLifecyclesPanel lifecycles={data.changes.orderLifecycles} />
              )}
              {changesTab === 'timeline' && (
                <TimelinePanel events={data.changes.timeline} />
              )}
              {changesTab === 'quality' && (
                <DataQualityPanel report={data.dataQuality} />
              )}
            </div>
          </div>

          {/* Panel 3: Closing State */}
          <StateSnapshotCard
            title="Closing State"
            state={data.closingState}
            extra={
              <div className="flex items-center gap-4 text-xs text-muted-foreground pt-2 border-t border-border/30">
                <span className="text-success">+{data.closingState.ordersDelivered} delivered</span>
                <span className="text-warning">{data.closingState.ordersStillOnboard} onboard</span>
                <span className="text-ring">{data.closingState.ordersCarriedForward} carried forward</span>
              </div>
            }
          />
        </div>
      )}
    </div>
  );
}
